import { Controller } from '@hotwired/stimulus'
import { budgetGuidance, formatWholeBrl, wholeBrlToCents } from '../lib/offer_form.mjs'

const MAX_IMAGES = 5

export default class extends Controller {
  static targets = [
    'title', 'condition', 'description', 'priceInput', 'priceCents', 'budgetHint',
    'imageInput', 'photoSlot', 'previewTitle', 'previewDescription', 'previewPrice',
    'previewCondition', 'previewImage', 'previewImagePlaceholder'
  ]

  static values = {
    budgetCents: String,
    budgetLabel: String,
    buyerName: String,
    i18n: Object
  }

  connect() {
    this.selectedFiles = []
    this.objectUrls = []
    this.priceChanged()
    this.updatePreview()
    this.renderPhotoSlots()
  }

  disconnect() {
    this.revokeObjectUrls()
  }

  updatePreview() {
    const title = this.titleTarget.value.trim()
    const description = this.descriptionTarget.value.trim()

    this.previewTitleTarget.textContent = title || this.i18nValue.preview_title
    this.previewTitleTarget.classList.toggle('is-placeholder', !title)
    this.previewDescriptionTarget.textContent = description || this.i18nValue.preview_body
    this.previewConditionTarget.textContent = this.selectedOptionText(this.conditionTarget)
  }

  priceChanged() {
    const formatted = formatWholeBrl(this.priceInputTarget.value)
    const cents = wholeBrlToCents(formatted)

    this.priceInputTarget.value = formatted
    this.priceCentsTarget.value = cents || ''
    this.priceInputTarget.setCustomValidity(formatted && !cents ? this.i18nValue.invalid_price : '')
    this.previewPriceTarget.textContent = formatted
      ? this.interpolate(this.i18nValue.preview_price, { AMOUNT: formatted })
      : this.i18nValue.preview_price_empty
    this.updateBudgetHint(cents)
  }

  submit() {
    this.priceChanged()
    this.syncImageInput()
  }

  chooseImages(event) {
    const index = Number(event.currentTarget.dataset.index)
    if (this.selectedFiles[index]) {
      this.selectedFiles.splice(index, 1)
      this.syncImageInput()
      this.renderPhotoSlots()
    } else {
      this.imageInputTarget.click()
    }
  }

  imagesChanged() {
    const additions = Array.from(this.imageInputTarget.files)
    const knownFiles = new Set(this.selectedFiles.map((file) => this.fileKey(file)))
    const uniqueAdditions = additions.filter((file) => !knownFiles.has(this.fileKey(file)))
    const availableSlots = MAX_IMAGES - this.selectedFiles.length

    uniqueAdditions.slice(0, availableSlots).forEach((file) => this.selectedFiles.push(file))
    if (uniqueAdditions.length > availableSlots) {
      window.alert(this.i18nValue.maximum_images)
    }

    this.syncImageInput()
    this.renderPhotoSlots()
  }

  updateBudgetHint(priceCents) {
    const guidance = budgetGuidance({
      priceCents,
      budgetCents: this.budgetCentsValue,
      budgetLabel: this.budgetLabelValue,
      buyerName: this.buyerNameValue,
      messages: {
        open: this.i18nValue.budget_open,
        empty: this.i18nValue.budget_empty,
        within: this.i18nValue.budget_within,
        above: this.i18nValue.budget_above
      }
    })

    this.budgetHintTarget.textContent = guidance.text
    this.budgetHintTarget.parentElement.dataset.tone = guidance.tone
  }

  selectedOptionText(select) {
    return select.selectedOptions[0]?.textContent.trim() || this.i18nValue.condition_placeholder
  }

  syncImageInput() {
    if (typeof DataTransfer === 'undefined') return

    const transfer = new DataTransfer()
    this.selectedFiles.forEach((file) => transfer.items.add(file))
    this.imageInputTarget.files = transfer.files
  }

  renderPhotoSlots() {
    this.revokeObjectUrls()
    let previewUrl

    this.photoSlotTargets.forEach((slot, index) => {
      const file = this.selectedFiles[index]
      slot.replaceChildren()

      if (file) {
        const objectUrl = URL.createObjectURL(file)
        this.objectUrls.push(objectUrl)
        previewUrl ||= objectUrl

        const image = document.createElement('img')
        image.src = objectUrl
        image.alt = ''

        const remove = document.createElement('span')
        remove.className = 'request-photo-slot__remove'
        remove.textContent = this.i18nValue.remove

        slot.append(image, remove)
        slot.classList.add('has-image')
        slot.setAttribute('aria-label', this.interpolate(this.i18nValue.remove_image, {
          NUMBER: index + 1,
          FILENAME: file.name
        }))
      } else {
        slot.append(this.imageIcon())
        slot.classList.remove('has-image')
        slot.setAttribute('aria-label', this.interpolate(this.i18nValue.add_image, { NUMBER: index + 1 }))
      }
    })

    if (previewUrl) {
      this.previewImageTarget.src = previewUrl
      this.previewImageTarget.hidden = false
      this.previewImagePlaceholderTarget.hidden = true
    } else {
      this.previewImageTarget.removeAttribute('src')
      this.previewImageTarget.hidden = true
      this.previewImagePlaceholderTarget.hidden = false
    }
  }

  imageIcon() {
    const namespace = 'http://www.w3.org/2000/svg'
    const svg = document.createElementNS(namespace, 'svg')
    svg.setAttribute('width', '20')
    svg.setAttribute('height', '20')
    svg.setAttribute('viewBox', '0 0 24 24')
    svg.setAttribute('fill', 'none')
    svg.setAttribute('aria-hidden', 'true')
    svg.innerHTML = '<rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="8.5" cy="9" r="1.6"/><path d="m21 16-4.5-4.5L7 21"/>'
    return svg
  }

  interpolate(template, values) {
    return Object.entries(values).reduce(
      (text, [key, value]) => text.replaceAll(`__${key}__`, value),
      template
    )
  }

  fileKey(file) {
    return `${file.name}:${file.size}:${file.lastModified}`
  }

  revokeObjectUrls() {
    this.objectUrls.forEach((url) => URL.revokeObjectURL(url))
    this.objectUrls = []
  }
}
